import React, { useEffect, useState, useRef } from "react";
import { useCallWeek, callWeekTekst } from "@/lib/useCallWeek";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, API, isPermanenteFout } from "@/lib/api";
import { currentWeek } from "@/lib/week";
import WeekPicker, { OmSelector } from "@/components/WeekPicker";
import AfspraakDialog from "@/components/AfspraakDialog";
import { MijnLocatiesSelect, useMyLocations } from "@/components/MijnLocaties";
import { usePersistentCollapse } from "@/hooks/usePersistentCollapse";
import { LoadError, errMessage } from "@/components/LoadError";
import { registerFlush } from "@/lib/flushRegistry";
import { useSmal } from "@/lib/useSmal";
import { TEGEL, TEGEL_WIT, TEGEL_LABEL, TEGEL_ROOD, TEGEL_GETAL, getalKleur } from "@/components/rust/Rust";
import { ChevronDown, ChevronUp, Save, AlertTriangle, Plus, MessageCircleQuestion, CheckCircle2, User, ClipboardCheck, CalendarCheck, Mail } from "lucide-react";
import { MailConcept, GeenAdresHint } from "@/components/MailConcept";
import { openMailConcept, selectNode } from "@/lib/mailConcept";
import { toast } from "sonner";
import { KopChips, LocatieCijfer, Lade, LooptNog, CheckRij, nietDezeWeekTekst, FoutRegel, cijfersVan } from "@/components/locatiecall/KaartParts";
import { Strook } from "@/components/overzicht/Strook";
import { LocatieCallUitleg } from "@/components/locatiecall/LocatieCallUitleg";
import { VorigeCallBalk } from "@/components/locatiecall/VorigeCallBalk";

const REPEAT_Q = "Kan jij herhalen wat we afgesproken hebben?";

// Read-only weergave van de door de bedrijfsleider aangeleverde call-voorbereiding.
// Aparte fetch per (week, locatie) — de OM leest, schrijft nooit. Niets pre-fillt in de OM-velden.
function CallPrepReadonly({ locatie, meta, prep, err }) {
  const kpiById = Object.fromEntries((meta.kpis || []).map((k) => [k.id, k]));
  const vol = prep?.volledigheid;

  return (
    <div data-testid={`prep-readonly-${locatie}`} className="rounded-md border border-blue-200 bg-blue-50/40 p-3">
      <div className="flex flex-wrap items-center justify-between gap-1.5 mb-2">
        <div className="flex items-center gap-1.5">
          <ClipboardCheck className="h-3.5 w-3.5 text-blue-600" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-blue-700">
            Voorbereiding — aangeleverd door de locatie{prep?.venster ? ` · call van ${prep.venster.callMaandagLabel}` : ""}
          </span>
        </div>
        {prep?.aangeleverd && vol && vol.totaal > 0 && (
          <span data-testid={`prep-bl-teller-${locatie}`} className={`text-xs font-bold tabular-nums px-2 py-0.5 rounded-full border ${vol.compleet ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
            {vol.ingevuld} van {vol.totaal} ingevuld
          </span>
        )}
      </div>
      {err ? (
        <p className="text-xs text-zinc-400">Voorbereiding kon niet geladen worden.</p>
      ) : !prep ? (
        <p className="text-xs text-zinc-400">Laden…</p>
      ) : !prep.aangeleverd ? (
        // Geen vals coachingsignaal: pas ná sluiting van het aanlevervenster is 'niets aangeleverd' een feit.
        prep.venster && !prep.venster.gesloten ? (
          <p data-testid={`prep-nog-${locatie}`} className="text-sm text-amber-700">Nog aan te leveren — venster sluit {prep.venster.closesAtLabel}.</p>
        ) : (
          <p data-testid={`prep-niets-${locatie}`} className="text-sm text-zinc-500 italic">Niets aangeleverd — bespreek dit als coaching-punt.</p>
        )
      ) : (
        <div className="space-y-2 text-sm" data-testid={`prep-inhoud-${locatie}`}>
          {(meta.kpis || []).map((k) => {
            const v = prep.kpis?.[k.id];
            if (!v || (!v.value && !v.antwoord)) return null;
            return (
              <div key={k.id}>
                <span className="font-semibold text-zinc-700">{kpiById[k.id]?.label || k.id}:</span>{" "}
                <span className="font-mono text-zinc-800">{v.value || "—"}</span>
                {v.antwoord && <p className="text-zinc-500 text-xs mt-0.5 whitespace-pre-wrap">{v.antwoord}</p>}
              </div>
            );
          })}
          {(() => {
            const checks = prep.checks || [];
            if (!checks.length) return null;
            return (
              <div className="space-y-1.5 pt-0.5" data-testid={`prep-bl-checks-${locatie}`}>
                {checks.map((c) => {
                  const antw = (prep.antwoorden || {})[c.id] || "";
                  const opm = (prep.opmerkingen || {})[c.id] || "";
                  const done = !!prep.prep?.[c.id];
                  return (
                    <div key={c.id} data-testid={`prep-bl-check-${locatie}-${c.id}`} className="text-sm">
                      <div className="flex items-start gap-1.5">
                        <span className={`mt-0.5 shrink-0 font-bold ${done ? "text-emerald-600" : "text-zinc-300"}`}>{done ? "✓" : "○"}</span>
                        <span className="font-semibold text-zinc-700">{c.text}</span>
                      </div>
                      <p className={`ml-5 whitespace-pre-wrap ${antw.trim() ? "text-zinc-800" : "text-amber-700 italic"}`}>
                        {antw.trim() || "— niet ingevuld"}
                      </p>
                      {opm.trim() && <p className="ml-5 text-xs text-zinc-500 whitespace-pre-wrap">Opmerking: {opm}</p>}
                    </div>
                  );
                })}
              </div>
            );
          })()}
          {(prep.eigenPunt || "").trim() && (
            <div><span className="font-semibold text-zinc-700">Eigen inbreng:</span> <span className="text-zinc-500 whitespace-pre-wrap">{prep.eigenPunt}</span></div>
          )}
          {prep.updatedBy && <p className="text-[10px] text-zinc-400">door {prep.updatedBy}</p>}
        </div>
      )}
    </div>
  );
}

function emptyCall(week, om, locatie) {
  return {
    week, om, locatie, opstart: false,
    omzet: null, omzetReflectie: "",
    pk: null, pkReflectie: "",
    bijverkoop: null, bijverkoopReflectie: "",
    prep: {}, prepNotes: {}, prepOpmerkingen: {}, verslag: false, verslagNotitie: "", wow: null, bevinding: "",
    rood: false, isRood: false, roodZonderAfspraak: false, bevindingFlag: false,
  };
}

function JaNee({ value, onChange, jaVal, neeVal, disabled, testid }) {
  return (
    <div className="flex gap-1.5 w-full sm:w-auto" data-testid={testid}>
      <button
        type="button" disabled={disabled} onClick={() => onChange(value === jaVal ? null : jaVal)}
        className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-semibold border transition-colors ${value === jaVal ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-zinc-300 text-zinc-500 hover:border-zinc-400"} ${disabled ? "opacity-60" : ""}`}
      >Ja</button>
      <button
        type="button" disabled={disabled} onClick={() => onChange(value === neeVal ? null : neeVal)}
        className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-semibold border transition-colors ${value === neeVal ? "bg-rose-600 border-rose-600 text-white" : "bg-white border-zinc-300 text-zinc-500 hover:border-zinc-400"} ${disabled ? "opacity-60" : ""}`}
      >Nee</button>
    </div>
  );
}

const sectionLabel = "text-[11px] font-semibold uppercase tracking-wider text-zinc-400";

// 1f — werklijst voor de OM: welke locaties hebben nog niets aangeleverd voor de EERSTVOLGENDE call.
// Read-only en server-afgeleid; vóór sluiting een 'nog te doen'-lijst, erna exact de coaching-set.
function NietAangeleverd() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = () => {
    setErr("");
    api.get("/call-prep/openstaand").then((r) => setD(r.data)).catch((e) => setErr(errMessage(e)));
  };
  useEffect(load, []);
  if (err) return <div className="mb-4"><LoadError error={err} onRetry={load} testid="openstaand-error" /></div>;
  if (!d) return null;
  const v = d.venster || {};
  return (
    <div data-testid="niet-aangeleverd-blok" className="mb-4 rounded-[10px] border border-zinc-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <ClipboardCheck className="h-3.5 w-3.5 text-blue-600" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
            Nog niets aangeleverd voor de call van {v.callMaandagLabel}
          </span>
        </div>
        <span className="text-[11px] text-zinc-400">{v.gesloten ? `venster gesloten (${v.closesAtLabel})` : `venster sluit ${v.closesAtLabel}`}</span>
      </div>
      {d.nietAangeleverd.length === 0 ? (
        <p data-testid="niet-aangeleverd-leeg" className="text-sm text-emerald-700 mt-1.5">Alle locaties met een bedrijfsleider hebben aangeleverd.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="niet-aangeleverd-lijst">
          {d.nietAangeleverd.map((r) => (
            <span key={r.locatie} data-testid={`niet-aangeleverd-${r.locatie}`}
              className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border ${v.gesloten ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
              {r.locatie}
            </span>
          ))}
        </div>
      )}
      {d.zonderBedrijfsleider.length > 0 && (
        <p className="text-[11px] text-zinc-400 mt-2" data-testid="zonder-bedrijfsleider">
          Zonder bedrijfsleider-account (kunnen niet aanleveren): {d.zonderBedrijfsleider.join(", ")}
        </p>
      )}
      {(d.onvolledig || []).length > 0 && (
        // Zelfde venster-regel als 'niets aangeleverd': vóór sluiting neutraal, erna een coachingpunt.
        <p className={`text-xs mt-2 ${v.gesloten ? "text-rose-700" : "text-amber-800"}`} data-testid="onvolledig-aangeleverd">
          <span className="font-semibold">{v.gesloten ? "Onvolledig aangeleverd — coachingpunt: " : "Nog niet compleet: "}</span>
          {d.onvolledig.map((o) => `${o.locatie} (${o.ingevuld}/${o.totaal})`).join(", ")}
        </p>
      )}
    </div>
  );
}

// A4: read-only weekritme-detail van de bedrijfsleider op deze locatie — de OM ziet WELKE taken open
// zijn (coaching-gesprek), niet enkel de score. Nooit bewerkbaar; niets voor een locatie zonder bl-account.
function BlWeekritmeReadonly({ week, locatie }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = () => {
    setD(null); setErr("");
    api.get(`/weekritme/bl-detail?week=${week}&locatie=${encodeURIComponent(locatie)}`)
      .then((r) => setD(r.data))
      .catch((e) => setErr(errMessage(e)));
  };
  useEffect(load, [week, locatie]);

  // Fout ≠ "geen bedrijfsleider": een mislukte load toont een retry, geen stilte.
  if (err) return <div className="mt-1"><LoadError error={err} onRetry={load} testid={`bl-weekritme-detail-error-${locatie}`} /></div>;
  if (d && !d.hasAccount) return null; // geen bedrijfsleider-account → niets tonen
  return (
    <div data-testid={`bl-weekritme-detail-${locatie}`} className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5">
          <CalendarCheck className="h-3.5 w-3.5 text-emerald-600" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Weekritme bedrijfsleider · week {week.slice(6)}</span>
        </div>
        {d && d.total > 0 && (
          <span className="font-mono text-xs font-bold text-zinc-700 tabular-nums">{d.done}/{d.total}</span>
        )}
      </div>
      {!d ? (
        <p className="text-xs text-zinc-400">Laden…</p>
      ) : d.total === 0 ? (
        <p className="text-sm text-zinc-500 italic">Weekritme-profiel is nog niet ingericht.</p>
      ) : (
        <ul className="space-y-1">
          {d.tasks.map((t) => (
            <li key={t.id} data-testid={`bl-wr-task-${locatie}-${t.id}`} className="flex items-start gap-2 text-sm">
              <span className={`mt-0.5 h-4 w-4 shrink-0 rounded-sm border flex items-center justify-center ${t.done ? "bg-emerald-500 border-emerald-500" : "border-zinc-300 bg-white"}`}>
                {t.done && <CheckCircle2 className="h-3 w-3 text-white" strokeWidth={3} />}
              </span>
              <span className="min-w-0">
                <span className={t.done ? "text-zinc-400 line-through" : "text-zinc-700"}>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mr-1.5">{t.day?.slice(0, 2)}</span>{t.text}
                </span>
                {t.toelichting && <span data-testid={`bl-wr-toelichting-${locatie}-${t.id}`} className="block text-xs text-zinc-500 whitespace-pre-line break-words">{t.toelichting}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const keyOf = (c) => `${c.week}|${c.om}|${c.locatie}`;

// R17: scroll naar een kaart met scroll-margin-top = hoogte van de sticky kop (+ wat lucht).
const scrollNaarKaart = (loc, smooth) => {
  const el = document.querySelector(`[data-testid="locatie-card-${loc}"]`);
  if (!el) return;
  el.style.scrollMarginTop = `${(document.querySelector('[data-testid="sticky-kop"]')?.offsetHeight || 0) + 12}px`;
  el.scrollIntoView({ block: "start", behavior: smooth ? "smooth" : "auto" });
};

// v10-R1 R3: de kaart is gekeyd op week|om|locatie — een wissel unmount de oude kaart, die zijn eigen
// laatste wijziging (met zijn eigen refs) wegschrijft. Een snapshot van een andere sleutel wordt nooit verstuurd.
// v21 K4: wat nog nodig is voor klaar; de server rekent (call_staat), alleen voor calls vanaf klaarRegelsVanaf.
function KlaarRegel({ staat, locatie }) {
  if (!staat?.nieuweRegels) return null;
  return (
    <p data-testid={`klaar-regel-${locatie}`} className={`text-sm font-semibold rounded-md border px-3 py-2 ${staat.klaar ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-900"}`}>
      {staat.klaar ? "Klaar" : `Nog nodig voor klaar: ${staat.nogNodig.join(" · ")}`}
      {" "}{staat.teLaat && <span className="text-rose-700">· te laat{staat.grensLabel ? ` (grens ${staat.grensLabel})` : ""}</span>}
    </p>
  );
}

function LocationCard({ call, meta, checks, nietDeze, kaartErr, editable, loading, onSave, onMerge, onAddAfspraak, open, onToggle, blScore, kaartInfo, overWeekNr, onNext, lopendVersie, lades, geladen = true }) {
  const { user } = useAuth();
  const [local, setLocal] = useState(call);
  const [status, setStatus] = useState("idle"); // idle | dirty | saving | saved
  const [savedAt, setSavedAt] = useState(null);

  const localRef = useRef(local);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const timerRef = useRef(null);
  const onSaveRef = useRef(onSave);
  const editableRef = useRef(editable);
  const keyRef = useRef(keyOf(call));
  const unmountedRef = useRef(false);
  const failRef = useRef(0);
  const reeksRef = useRef(0);   // v24 R5: mislukte saves op rij → langer wachten (1, 2, 4, 8, 15 s)
  const permanentRef = useRef(false);   // v24-R1 F4: de server weigert → niet herhalen tot de volgende wijziging

  useEffect(() => { localRef.current = local; }, [local]);
  useEffect(() => { onSaveRef.current = onSave; });
  const onMergeRef = useRef(onMerge);
  useEffect(() => { onMergeRef.current = onMerge; });
  useEffect(() => { editableRef.current = editable; });

  // Accept incoming server data (zelfde sleutel), but never clobber unsaved local edits.
  useEffect(() => {
    if (!dirtyRef.current && keyOf(call) === keyRef.current) setLocal(call);
  }, [call]);

  // keepalive: de browser maakt de PUT af, ook als de kaart (of de pagina) intussen weg is.
  const keepaliveSend = () => {
    const snapshot = localRef.current;
    if (keyOf(snapshot) !== keyRef.current) return;
    const token = localStorage.getItem("tw1_token");
    try {
      return fetch(`${API}/locatie-calls`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(snapshot),
        keepalive: true,
      });
    } catch { return Promise.reject(new Error("keepalive")); }
  };
  // R2-F2: kaart is weg → bij succes de call overnemen (alleen als hij nog bij week/OM hoort), bij een fout een toast.
  const keepaliveMetUitkomst = () => {
    const loc = localRef.current.locatie;
    const p = keepaliveSend();
    if (!p) return;
    p.then(async (res) => { if (!res.ok) throw new Error(String(res.status)); onMergeRef.current(await res.json()); })
      .catch(() => toast.error(`Wijziging voor ${loc} kon niet worden opgeslagen. Open de kaart opnieuw.`));
  };

  const doSave = async (silent) => {
    if (!editableRef.current || savingRef.current) return;
    const snapshot = localRef.current;
    if (keyOf(snapshot) !== keyRef.current) return;   // R3: nooit een snapshot van een andere week/OM/locatie
    savingRef.current = true;
    setStatus("saving");
    try {
      await onSaveRef.current(snapshot, silent);
      reeksRef.current = 0;
      if (localRef.current === snapshot) {
        dirtyRef.current = false;
        setStatus("saved");
        setSavedAt(new Date());
      }
    } catch (e) {
      failRef.current += 1;
      reeksRef.current += 1;
      permanentRef.current = isPermanenteFout(e);
      setStatus("dirty");
      if (!silent || permanentRef.current) toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      savingRef.current = false;
      // Edits tijdens de save (of een overgeslagen flush) → dirty bleef true → opnieuw inplannen (#11).
      // Kaart intussen weg (wissel tijdens een save)? Dan direct via keepalive, niet via een timer.
      if (dirtyRef.current && editableRef.current) {
        if (unmountedRef.current) { keepaliveMetUitkomst(); dirtyRef.current = false; }
        else if (!permanentRef.current) scheduleSave(reeksRef.current ? Math.min(1000 * 2 ** (reeksRef.current - 1), 15000) : 1000, true);
      }
    }
  };

  const scheduleSave = (vertraging = 1000, herhaling = false) => {
    if (!editableRef.current || unmountedRef.current) return;
    if (!herhaling) permanentRef.current = false;   // een nieuwe wijziging mag het opnieuw proberen
    dirtyRef.current = true;
    setStatus("dirty");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSave(true), vertraging);
  };

  const flushNow = () => {
    clearTimeout(timerRef.current);
    // Loopt er al een save? Dan niets doen — dirty blijft true en doSave's finally plant 'm opnieuw in (#11/#12).
    if (dirtyRef.current && !savingRef.current && !permanentRef.current) doSave(true);   // v24-R1 F4: weigering → pas bij een nieuwe wijziging
  };

  // v8-C: wacht tot alles wat getypt is echt opgeslagen is (voor 'Mail klaarzetten').
  const flushAndWait = async () => {
    clearTimeout(timerRef.current);
    const fouten = failRef.current;
    for (let i = 0; i < 50 && savingRef.current; i++) await new Promise((r) => setTimeout(r, 100));
    // R2-F1: mislukte de lopende save, dan niet meteen opnieuw — de aanroeper toont de toast, de timer probeert straks opnieuw.
    if (failRef.current !== fouten) return;
    if (dirtyRef.current && !savingRef.current) await doSave(true);
    for (let i = 0; i < 50 && savingRef.current; i++) await new Promise((r) => setTimeout(r, 100));
  };
  const flushWachtRef = useRef(flushAndWait);
  flushWachtRef.current = flushAndWait;
  useEffect(() => registerFlush(() => flushWachtRef.current()), []);   // v17-R1 D4

  const [mailBusy, setMailBusy] = useState(false);
  const [mailFallback, setMailFallback] = useState(null);
  const [geenAdres, setGeenAdres] = useState(false);
  const mailRef = useRef(null);
  // R12: concept/hint horen bij deze kaart in deze week — wissen bij elke contextwissel.
  useEffect(() => { setMailFallback(null); setGeenAdres(false); }, [call.week, call.om, call.locatie]);
  const ctxKey = `${call.week}|${call.om}|${call.locatie}`;
  const ctxRef = useRef(ctxKey);
  ctxRef.current = ctxKey;
  const mailKlaarzetten = async () => {
    const klikCtx = ctxRef.current;   // S5: resultaat alleen toepassen als week/OM/locatie sinds de klik gelijk bleven
    setMailBusy(true);
    setMailFallback(null);
    setGeenAdres(false);
    try {
      await flushAndWait();
      if (dirtyRef.current) {   // R10: opslaan mislukte → geen mail zonder het laatst getypte
        toast.error("Je laatste wijziging is nog niet opgeslagen. Probeer het zo opnieuw.");
        return;
      }
      const r = await api.get(`/weekrapport/mail?locatie=${encodeURIComponent(call.locatie)}&week=${encodeURIComponent(localRef.current.week || call.week)}&om=${encodeURIComponent(user.username)}`);
      if (ctxRef.current !== klikCtx) return;
      setGeenAdres(!r.data.aan?.email);
      await openMailConcept(r.data, {
        locatie: call.locatie, isOversight: user.role === "oversight",
        onCopyFailed: () => {
          if (ctxRef.current !== klikCtx) return;
          setMailFallback(r.data); setTimeout(() => selectNode(mailRef.current), 50);
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mail klaarzetten mislukt");
    } finally {
      setMailBusy(false);
    }
  };

  // Flush pending edits on unmount (navigeren mag niets verliezen) via axios (SPA blijft leven).
  // Bij tab sluiten/verbergen: fetch + keepalive zodat de browser de PUT afmaakt (axios wordt niet afgewacht).
  useEffect(() => {
    unmountedRef.current = false;   // R2-F1: StrictMode draait de cleanup één keer bij mount — zet hem terug
    const keepaliveFlush = (clearDirty) => {
      // Respecteer een lopende save (#12): geen tweede gelijktijdige PUT voor dezelfde kaart.
      if (!dirtyRef.current || !editableRef.current || savingRef.current) return;
      clearTimeout(timerRef.current);
      keepaliveSend();
      // Alleen bij een échte page-unload optimistisch wissen; bij visibilitychange blijft de pagina leven,
      // dus dirty NIET wissen zodat een latere save/unmount 'm alsnog opslaat als deze faalde (#6).
      if (clearDirty) dirtyRef.current = false;
    };
    const unmountFlush = () => {
      unmountedRef.current = true;
      clearTimeout(timerRef.current);
      // R3: de oude kaart verstuurt zijn eigen snapshot (keepalive); loopt er een save, dan doet die's finally het.
      if (dirtyRef.current && editableRef.current && !savingRef.current) {
        keepaliveMetUitkomst();
        dirtyRef.current = false;
      }
    };
    const onBeforeUnload = () => keepaliveFlush(true);
    const onVisibility = () => {
      if (document.visibilityState === "hidden") keepaliveFlush(false);
      // v24-R1 F3: terug zichtbaar en nog niet opgeslagen → de herhaling weer starten
      else if (dirtyRef.current && editableRef.current && !savingRef.current && !permanentRef.current) scheduleSave(1000, true);
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("visibilitychange", onVisibility);
      unmountFlush();
    };
  }, []);

  const set = (k, v) => { setLocal((c) => ({ ...c, [k]: v })); scheduleSave(); };
  const setPrep = (id, v) => { setLocal((c) => ({ ...c, prep: { ...(c.prep || {}), [id]: v } })); scheduleSave(); };
  const setPrepNote = (id, v) => { setLocal((c) => ({ ...c, prepNotes: { ...(c.prepNotes || {}), [id]: v } })); scheduleSave(); };
  const setPrepOpmerking = (id, v) => { setLocal((c) => ({ ...c, prepOpmerkingen: { ...(c.prepOpmerkingen || {}), [id]: v.slice(0, 300) } })); scheduleSave(); };

  const kpiField = { omzet: ["omzet", "omzetReflectie"], pk: ["pk", "pkReflectie"], bijverkoop: ["bijverkoop", "bijverkoopReflectie"] };

  const touched = call.opstart || call.omzet || call.pk || call.bijverkoop || call.wow;
  const inputCls = "w-full px-3 py-2 rounded-md border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm text-zinc-900 disabled:bg-zinc-50";
  const savedTime = savedAt ? savedAt.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }) : "";

  const ladeOpen = (k) => !lades.isCollapsed(`${call.locatie}|${k}`);
  const ladeToggle = (k) => lades.toggle(`${call.locatie}|${k}`);
  const [blPrep, setBlPrep] = useState(null);
  const [prepErr, setPrepErr] = useState("");
  const [prepTick, setPrepTick] = useState(0);
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setBlPrep(null); setPrepErr("");
    api.get(`/call-prep?week=${call.week}&locatie=${encodeURIComponent(call.locatie)}`)
      .then((r) => { if (alive) setBlPrep(r.data); })
      .catch((e) => { if (alive) setPrepErr(errMessage(e)); });
    return () => { alive = false; };
  }, [open, call.week, call.locatie, prepTick]);
  const [toelOpen, setToelOpen] = useState({});
  const notitieRef = useRef(null);
  const selRef = useRef(null);
  const onzetMouseDown = (e) => {
    e.preventDefault();
    const el = notitieRef.current;
    selRef.current = el && document.activeElement === el && el.selectionEnd > el.selectionStart ? [el.selectionStart, el.selectionEnd] : null;
  };
  const maakAfspraak = () => {
    const v = local.verslagNotitie || "";
    const sel = selRef.current ? v.slice(selRef.current[0], selRef.current[1]) : "";
    selRef.current = null;
    onAddAfspraak(call.locatie, sel.trim() ? sel : v);
  };
  const [volgendeBusy, setVolgendeBusy] = useState(false);
  const volgende = async () => {
    setVolgendeBusy(true);
    try {
      await flushAndWait();
      if (dirtyRef.current) { toast.error("Je laatste wijziging is nog niet opgeslagen. Probeer het zo opnieuw."); return; }
      onNext(call.locatie);
    } finally {
      setVolgendeBusy(false);
    }
  };
  // v18 B1: bij het openen van de kaart staan checks zonder eigen antwoord open, ingevulde dicht; daarna alleen de gebruiker.
  const smal = useSmal();
  const [checkOpen, setCheckOpen] = useState(null);
  useEffect(() => { if (!open) setCheckOpen(null); }, [open]);
  useEffect(() => {
    if (!open || !checks || !geladen || checkOpen) return;
    const n = { ...(call.prepNotes || {}), ...(dirtyRef.current ? localRef.current.prepNotes || {} : {}) };
    setCheckOpen(Object.fromEntries(checks.map((p) => [p.id, !(n[p.id] || "").trim()])));
  }, [open, checks, geladen, checkOpen, call.prepNotes]);
  const nCijfers = cijfersVan(local);
  const nAntw = (checks || []).filter((p) => ((local.prepNotes || {})[p.id] || "").trim()).length;
  const blVol = blPrep?.volledigheid;
  const blMoment = blPrep?.updatedAt ? new Date(blPrep.updatedAt).toLocaleString("nl-NL", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <div data-testid={`locatie-card-${call.locatie}`} className={`bg-white rounded-[10px] border overflow-hidden transition-colors ${call.isRood ? "border-rose-300 ring-1 ring-rose-200" : "border-zinc-200 hover:border-zinc-300"}`}>
      <button onClick={onToggle} className="w-full flex items-center justify-between gap-2 px-4 py-3.5 hover:bg-zinc-50 transition-colors">
        <div className="flex flex-wrap items-center gap-2 text-left">
          <span className={`h-2 w-2 rounded-full ${call.isRood ? "bg-rose-500" : touched ? "bg-emerald-500" : "bg-zinc-300"}`} />
          <span className="font-heading font-bold text-zinc-900">{call.locatie}</span>
          <KopChips call={call} info={kaartInfo} />
          {kaartInfo?.reeks && <span className="w-full sm:w-auto"><Strook r={kaartInfo} testid={`kaart-strook-${call.locatie}`} /></span>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {loading && <span data-testid={`loading-status-${call.locatie}`} className="text-[11px] font-medium text-zinc-400">Laden…</span>}
          {editable && status !== "idle" && (
            <span data-testid={`autosave-status-${call.locatie}`} className={`text-[11px] font-medium ${status === "saving" ? "text-blue-600" : status === "saved" ? "text-emerald-600" : "text-zinc-400"}`}>
              {status === "saving" ? "Bezig met opslaan…" : status === "saved" ? `Opgeslagen ✓ ${savedTime}` : "Niet opgeslagen"}
            </span>
          )}
          {open ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-5 border-t border-zinc-100 pt-4 space-y-5">
          <KlaarRegel staat={call.staat || kaartInfo?.staatLeeg} locatie={call.locatie} />
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" disabled={!editable} checked={!!local.opstart} onChange={(e) => set("opstart", e.target.checked)} data-testid={`opstart-${call.locatie}`} className="h-4 w-4 rounded-sm accent-blue-600" />
            <span className="text-sm font-medium text-zinc-700">Ingecheckt bij de bedrijfsleider</span>
          </label>

          {prepErr && <FoutRegel testid={`prep-load-error-${call.locatie}`} tekst="Aanlevering kon niet geladen worden" onRetry={() => setPrepTick((t) => t + 1)} />}
          <div className="space-y-2.5" data-testid={`cijfers-${call.locatie}`}>
            <div className={sectionLabel}>De cijfers{overWeekNr ? ` — over week ${overWeekNr}` : ""}</div>
            {meta.kpis.map((kpi) => {
              const [vField, rField] = kpiField[kpi.id];
              const toonToel = local[vField] === "niet" || (local[rField] || "").trim() || toelOpen[kpi.id];
              return (
                <div key={kpi.id} className="rounded-md border border-zinc-200 p-3 bg-zinc-50/50">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                    <div className="min-w-0">
                      <div className="font-heading text-sm font-bold text-zinc-800">{kpi.label} <span className="font-sans font-medium text-zinc-500">· {overWeekNr ? `Week ${overWeekNr} gehaald?` : "Gehaald?"}</span></div>
                      <LocatieCijfer prep={blPrep} err={!!prepErr} kpiId={kpi.id} locatie={call.locatie} />
                    </div>
                    <JaNee testid={`kpi-${kpi.id}-${call.locatie}`} value={local[vField]} jaVal="behaald" neeVal="niet" disabled={!editable} onChange={(v) => set(vField, v)} />
                  </div>
                  {toonToel ? (
                    <textarea disabled={!editable} rows={2} placeholder="Toelichting…" value={local[rField] || ""} onChange={(e) => set(rField, e.target.value)} onBlur={flushNow} data-testid={`kpi-toelichting-${kpi.id}-${call.locatie}`} className={`${inputCls} mt-2`} />
                  ) : editable && (
                    <button type="button" data-testid={`kpi-toelichting-open-${kpi.id}-${call.locatie}`} onClick={() => setToelOpen((t) => ({ ...t, [kpi.id]: true }))} className="text-xs font-semibold text-blue-700 hover:text-blue-900 mt-1.5">+ Toelichting</button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="rounded-md border border-zinc-200 p-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mb-2">
              <div className="font-heading text-sm font-bold text-zinc-800">{meta.texts?.wowLabel || "WOW"}{(call.staat || kaartInfo?.staatLeeg)?.nieuweRegels && <span data-testid={`wow-verplicht-${call.locatie}`} className="ml-1.5 font-sans text-xs font-semibold text-rose-600">* verplicht voor klaar</span>}</div>
              <JaNee testid={`wow-${call.locatie}`} value={local.wow} jaVal="ja" neeVal="nee" disabled={!editable} onChange={(v) => set("wow", v)} />
            </div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Bevinding <span className="text-rose-500">*</span> (vrije tekst — wat heb je echt waargenomen?)</label>
            <textarea disabled={!editable} rows={2} value={local.bevinding || ""} onChange={(e) => set("bevinding", e.target.value)} onBlur={flushNow} data-testid={`bevinding-${call.locatie}`}
              className={`${inputCls} ${local.wow && !(local.bevinding || "").trim() ? "border-amber-400 ring-1 ring-amber-200" : ""}`} placeholder="Verplicht — je kunt geen waarneming faken die je niet hebt gedaan." />
            {local.wow && !(local.bevinding || "").trim() && <p className="text-xs text-amber-700 mt-1 font-medium">WOW is beantwoord maar bevinding ontbreekt.</p>}
          </div>

          <div className="space-y-2" data-testid={`checks-${call.locatie}`}>
            <div className="flex items-center justify-between gap-2">
              <div className={sectionLabel}>Checks — voorbereiden en in de call afvinken</div>
              {checks?.length > 0 && (
                <span data-testid={`prep-teller-${call.locatie}`} className={`text-xs font-bold tabular-nums px-2 py-0.5 rounded-full border ${nAntw === checks.length ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                  {nAntw} van {checks.length} voorbereid
                </span>
              )}
            </div>
            {!checks ? (
              kaartErr ? <p data-testid={`checks-fout-${call.locatie}`} className="text-xs text-zinc-500">De checks konden niet geladen worden; zie de melding bovenaan.</p>
                : <p className="text-xs text-zinc-400">Laden…</p>
            ) : checks.length === 0 ? (
              <p data-testid={`checks-geen-${call.locatie}`} className="text-xs text-zinc-400 italic">Geen checks deze week.</p>
            ) : (
              <ul className="divide-y divide-zinc-200 rounded-md border border-zinc-200">
                {checks.map((p) => (
                  <CheckRij key={p.id} p={p} local={local} prep={blPrep} prepErr={!!prepErr} editable={editable} locatie={call.locatie} inputCls={inputCls}
                    wie={call.om === user?.username ? "Jij" : (call.om ? call.om.charAt(0).toUpperCase() + call.om.slice(1) : "OM")} eigen={call.om === user?.username}
                    vorige={kaartInfo?.vorigeKeer?.[p.id]} setPrep={setPrep} setPrepNote={setPrepNote} setPrepOpmerking={setPrepOpmerking} flushNow={flushNow}
                    compact={smal} uit={checkOpen?.[p.id] ?? true} onToggle={() => setCheckOpen((o) => ({ ...(o || {}), [p.id]: !(o?.[p.id] ?? true) }))} />
                ))}
              </ul>
            )}
            {nietDeze?.length > 0 && <p data-testid={`checks-niet-deze-week-${call.locatie}`} className="text-xs text-zinc-400">Niet deze week: {nietDezeWeekTekst(nietDeze)}.</p>}
          </div>

          <LooptNog locatie={call.locatie} versie={lopendVersie} roodZonderAfspraak={call.roodZonderAfspraak} editable={editable} onAddAfspraak={onAddAfspraak} />

          <div className="space-y-1.5">
            <div className={sectionLabel}>Gespreksnotitie</div>
            <textarea ref={notitieRef} disabled={!editable} rows={3} placeholder={meta.texts?.repeatQuestion || REPEAT_Q} value={local.verslagNotitie || ""} onChange={(e) => set("verslagNotitie", e.target.value)} onBlur={flushNow} data-testid={`verslag-notitie-${call.locatie}`} className={inputCls} />
            {editable && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <button data-testid={`verslag-to-afspraak-${call.locatie}`} onMouseDown={onzetMouseDown} onClick={maakAfspraak}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-300 text-sm font-semibold text-zinc-700 hover:border-blue-400 hover:text-blue-700 transition-colors">
                  <Plus className="h-4 w-4" /> Maak er een afspraak van
                </button>
                <span className="text-xs text-zinc-400">Selecteer een regel om alleen die om te zetten.</span>
              </div>
            )}
          </div>

          <div data-testid={`voet-${call.locatie}`} className="flex flex-wrap items-center gap-2 border-t border-zinc-100 pt-4">
            <span data-testid={`voet-cijfers-${call.locatie}`} className={`text-xs font-bold px-2 py-1 rounded-full border ${nCijfers === 4 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : nCijfers > 0 ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>Cijfers {nCijfers} van 4</span>
            <label className="flex items-center gap-2 cursor-pointer px-1">
              <input type="checkbox" disabled={!editable} checked={!!local.verslag} onChange={(e) => set("verslag", e.target.checked)} data-testid={`verslag-${call.locatie}`} className="h-4 w-4 rounded-sm accent-blue-600" />
              <span className="text-sm text-zinc-700">Gespreksverslag gemaakt</span>
            </label>
            {editable && (
              <button data-testid={`mail-klaarzetten-${call.locatie}`} onClick={mailKlaarzetten} disabled={mailBusy} className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-300 text-sm font-semibold text-zinc-800 hover:border-zinc-500 disabled:opacity-60 transition-colors">
                <Mail className="h-4 w-4" /> {mailBusy ? "Bezig…" : "Mail klaarzetten"}
              </button>
            )}
            {editable && (
              <button data-testid={`save-call-${call.locatie}`} onClick={() => { clearTimeout(timerRef.current); doSave(false); }} disabled={status === "saving" || status !== "dirty"} className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-300 text-sm font-semibold text-zinc-700 disabled:opacity-50">
                <Save className="h-4 w-4" /> {status === "saving" ? "Opslaan…" : status === "dirty" ? "Opslaan" : "Alles opgeslagen"}
              </button>
            )}
            {editable && (
              <button data-testid={`volgende-locatie-${call.locatie}`} onClick={volgende} disabled={volgendeBusy} className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-md bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-60 transition-colors">
                {volgendeBusy ? "Bezig…" : <>Volgende locatie <ChevronDown className="h-4 w-4 -rotate-90" /></>}
              </button>
            )}
          </div>
          {editable && geenAdres && !mailFallback && <GeenAdresHint locatie={call.locatie} isOversight={user.role === "oversight"} testid={`mail-geen-adres-${call.locatie}`} />}
          {editable && mailFallback && <MailConcept ref={mailRef} mail={mailFallback} locatie={call.locatie} isOversight={user.role === "oversight"} />}

          <div className="space-y-2" data-testid={`naslag-${call.locatie}`}>
            <div className={sectionLabel}>Naslag · voor en na de call</div>
            <Lade testid={`lade-aangeleverd-${call.locatie}`} open={ladeOpen("bl")} onToggle={() => ladeToggle("bl")}
              titel={`Aangeleverd door de locatie${blPrep?.aangeleverd && blVol?.totaal > 0 ? ` · ${blVol.ingevuld} van ${blVol.totaal}` : ""}${blMoment ? ` · ${blMoment}` : ""}`}>
              <CallPrepReadonly locatie={call.locatie} meta={meta} prep={blPrep} err={prepErr} />
            </Lade>
            {blScore && (
              <Lade testid={`lade-weekritme-${call.locatie}`} open={ladeOpen("wr")} onToggle={() => ladeToggle("wr")} titel={`Weekritme bedrijfsleider · ${blScore.total > 0 ? `${blScore.done} van ${blScore.total}` : "niet ingericht"}`}>
                <BlWeekritmeReadonly week={call.week} locatie={call.locatie} />
              </Lade>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LocatieCall() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  // v20-R1 R11: zonder ?week= is er nog geen week tot /meta er is; dan meteen standaardCallWeek (geen flits, geen oude-week-kaart)
  const [week, setWeekState] = useState(searchParams.get("week") || null);
  // v19-N6 F3: zonder ?week= volgt de pagina de server (na de calldag: de volgende call), tot je zelf een week kiest.
  const weekGekozen = useRef(!!searchParams.get("week"));
  const setWeek = (w) => { weekGekozen.current = true; setWeekState(w); };
  const isOmIdentity = user.role === "om" || (user.role === "oversight" && user.canDoOm);
  const [om, setOm] = useState(searchParams.get("om") || (isOmIdentity ? user.username : null));
  const [meta, setMeta] = useState({ locations: [], kpis: [], prepChecks: [], oms: [], inactiveOms: [], locationOms: {}, generalBuckets: [] });
  const [callsMap, setCallsMap] = useState({});
  const [loadedKey, setLoadedKey] = useState("");
  const [metaError, setMetaError] = useState("");
  const [callsError, setCallsError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLocatie, setDialogLocatie] = useState("");
  const [dialogWat, setDialogWat] = useState("");
  const { isCollapsed, toggle } = usePersistentCollapse("locatie-call", user.username, true);
  // R3/R17: één gedeelde lade-staat voor de hele pagina (overleeft het remounten van een kaart).
  const lades = usePersistentCollapse("locatie-call-lades", user.username, true);
  const { matches: locMatches } = useMyLocations(user.username);
  const isOm = isOmIdentity;

  const editable = isOmIdentity && om === user.username;

  // Refs naar de actuele context, zodat een save-merge oude-week/OM-data niet in de huidige weergave schrijft.
  const weekRef = useRef(week);
  const omRef = useRef(om);
  useEffect(() => { weekRef.current = week; }, [week]);
  useEffect(() => { omRef.current = om; }, [om]);

  const loadMeta = () => api.get("/meta").then((r) => {
      setMeta(r.data);
      setMetaError("");
      if (!weekGekozen.current && r.data.standaardCallWeek) setWeekState(r.data.standaardCallWeek);
      if (user.role !== "om") {
        // Behoud een gekozen (ook inactieve) OM; default nooit automatisch naar een inactieve OM.
        const valid = [...(r.data.oms || []), ...(r.data.inactiveOms || [])];
        setOm((cur) => (cur && valid.includes(cur) ? cur : (r.data.oms?.[0] || null)));
      }
    }).catch((e) => { setMetaError(errMessage(e)); if (!weekGekozen.current) setWeekState((w) => w || currentWeek()); });
  useEffect(() => { loadMeta(); }, [user.role]);

  const callsSeq = useRef(0);
  const loadCalls = () => {
    const seq = ++callsSeq.current;   // R3: alleen het antwoord op het láátste verzoek telt
    if (!om || !week) { setLoadedKey(""); setCallsMap({}); return; }
    // Wis eerst zodat de header-tellers nooit cijfers van de vorige week tonen bij een mislukte load.
    setLoadedKey("");
    setCallsMap({});
    const k = `${week}|${om}`;
    api.get(`/locatie-calls?week=${week}&om=${om}`).then((r) => {
      if (seq !== callsSeq.current) return;
      const map = {};
      for (const c of r.data) map[c.locatie] = c;
      setCallsMap(map);
      setLoadedKey(k);
      setCallsError("");
    }).catch((e) => { if (seq === callsSeq.current) setCallsError(errMessage(e)); });
  };
  const loaded = loadedKey === `${week}|${om}`;
  useEffect(loadCalls, [week, om]);

  // A4: weekritme-voortgang per locatie (bedrijfsleider) — read-only coaching-cijfer voor de OM.
  const [blScores, setBlScores] = useState({});
  const [blScoresError, setBlScoresError] = useState("");
  const blSeq = useRef(0);
  const loadBlScores = () => {
    const seq = ++blSeq.current;   // R2-F8: alleen het laatste antwoord telt
    if (!week) return;
    api.get(`/weekritme/bl-scores?week=${week}`)
      .then((r) => { if (seq !== blSeq.current) return; const m = {}; for (const s of r.data) if (!m[s.locatie] || s.done > m[s.locatie].done) m[s.locatie] = s; /* v19-N4: zelfde keuze als bl-detail (meeste afgevinkt, gelijk = eerste op naam) */ setBlScores(m); setBlScoresError(""); })
      .catch((e) => { if (seq !== blSeq.current) return; setBlScores({}); setBlScoresError(errMessage(e)); });
  };
  useEffect(loadBlScores, [week]);

  const getCall = (loc) => {
    const c = callsMap[loc];
    return c && c.week === week && c.om === om ? c : emptyCall(week, om, loc);
  };

  // Toon locaties die aan de gekozen OM zijn toegewezen + alle niet-toegewezen locaties (zichtbaar voor iedereen).
  // Algemene buckets (bv. Hoofdkantoor) horen NIET bij de wekelijkse locatie-call en worden hier uitgesloten.
  const buckets = new Set(meta.generalBuckets || []);
  const omAssigned = (loc) => {
    const assigned = (meta.locationOms || {})[loc];
    return !assigned || assigned.length === 0 || (om && assigned.includes(om));
  };
  // Onderscheid (ronde 3): GEEN toegewezen locaties is een ander probleem dan 'wel locaties, maar
  // je eigen selectie filtert ze weg'. Nul zichtbare locaties mag nooit als "Op koers" tellen.
  const assignedLocations = meta.locations.filter((l) => !buckets.has(l)).filter(omAssigned);
  // R17: 'Mijn locaties' van de kijker geldt alleen voor je eigen calls; ?locatie= (uit het dossier) toont
  // die kaart altijd, ook als hij (niet meer) aan deze OM is toegewezen.
  const eigen = om === user.username;
  // S2: alleen forceren zolang week én OM nog gelijk zijn aan de link waarmee je kwam; nooit een bucket.
  const urlLoc = searchParams.get("locatie");
  const focusLoc = urlLoc && !buckets.has(urlLoc) && week === searchParams.get("week") && om === searchParams.get("om") ? urlLoc : null;
  let visibleLocations = eigen ? assignedLocations.filter(locMatches) : assignedLocations;
  if (focusLoc && meta.locations.includes(focusLoc) && !visibleLocations.includes(focusLoc)) visibleLocations = [focusLoc, ...visibleLocations];
  const geenToewijzing = !metaError && !!om && assignedLocations.length === 0 && !visibleLocations.length;
  const allesWeggefilterd = eigen && !geenToewijzing && assignedLocations.length > 0 && visibleLocations.length === 0;
  const focusDone = useRef(false);
  useEffect(() => {
    if (!focusLoc || focusDone.current || !loaded || !meta.locations.includes(focusLoc)) return;
    focusDone.current = true;
    if (isCollapsed(focusLoc)) toggle(focusLoc);
    setTimeout(() => scrollNaarKaart(focusLoc, false), 250);
  }, [focusLoc, loaded, meta.locations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Alleen mergen als de opgeslagen call bij de HUIDIGE context hoort (anders schrijven we oude-week/OM-data in de nieuwe weergave).
  const mergeCall = (c) => {
    if (c && c.week === weekRef.current && c.om === omRef.current) setCallsMap((m) => ({ ...m, [c.locatie]: c }));
  };
  const callsMapRef = useRef(callsMap);
  callsMapRef.current = callsMap;
  const onSave = async (local, silent) => {
    const vorig = callsMapRef.current[local.locatie];
    const res = await api.put("/locatie-calls", local);
    mergeCall(res.data);
    if (!silent) toast.success(`${local.locatie} opgeslagen`);
    // R2-F5 + v21-R1 R5: kaartinfo opnieuw als compleetheid (x/4), rood of klaar veranderde.
    if (!vorig || cijfersVan(vorig) !== cijfersVan(res.data) || !!vorig.isRood !== !!res.data.isRood || !!vorig.staat?.klaar !== !!res.data.staat?.klaar) setKaartVersie((v) => v + 1);
  };

  const onAddAfspraak = (loc, wat = "") => { setDialogLocatie(loc); setDialogWat(wat); setDialogOpen(true); };
  // B2-7: volgende kaart in de lijstvolgorde openen en ernaartoe scrollen.
  const onNext = (loc) => {
    const i = visibleLocations.indexOf(loc);
    if (i < 0 || i === visibleLocations.length - 1) { toast("Dit was de laatste locatie."); return; }
    const next = visibleLocations[i + 1];
    if (kaartData.isCallDag) setKaartVersie((v) => v + 1);
    if (!isCollapsed(loc)) toggle(loc);
    if (isCollapsed(next)) toggle(next);
    setTimeout(() => scrollNaarKaart(next, true), 250);
  };

  const roodCount = Object.values(callsMap).filter((c) => c.isRood).length;
  const rzaCount = Object.values(callsMap).filter((c) => c.roodZonderAfspraak).length;
  const callWeek = useCallWeek(week);
  // v10-F1: uitlegkaart pas tonen als de vlag geladen is (geen flits voor wie hem al wegklikte)
  const [uitlegGezien, setUitlegGezien] = useState(null);
  const [uitleg, setUitleg] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get("/locatie-calls/uitleg").then((r) => { if (alive) { setUitlegGezien(r.data.gezien); setUitleg(!r.data.gezien); } })
      .catch(() => { if (alive) setUitlegGezien(true); });
    return () => { alive = false; };
  }, []);
  const begrepen = () => {
    setUitleg(false);
    setUitlegGezien(true);
    api.post("/locatie-calls/uitleg", { gezien: true }).catch(() => toast.error("Begrepen kon niet worden opgeslagen. Probeer het opnieuw."));
  };
  const [kaartData, setKaartData] = useState({});
  const [kaartVersie, setKaartVersie] = useState(0);
  const kaartGeldig = kaartData.week === week && kaartData.om === (om || null);
  const kaartInfo = kaartGeldig ? kaartData.locaties || {} : {};
  const [lopendVersie, setLopendVersie] = useState(0);
  const [kaartErr, setKaartErr] = useState(false);
  const paginaLocs = visibleLocations.join(",");
  useEffect(() => {
    let alive = true;
    if (!week) return undefined;
    api.get(`/locatie-calls/kaartinfo?week=${week}${om ? `&om=${encodeURIComponent(om)}` : ""}&locaties=${encodeURIComponent(paginaLocs)}`)
      .then((r) => { if (alive) { setKaartData(r.data); setKaartErr(false); } })
      .catch(() => { if (alive) setKaartErr(true); });   // v13-R1 F1: laatste goede kaartData houden
    return () => { alive = false; };
  }, [week, om, kaartVersie, paginaLocs]);
  const doneCount = Object.values(callsMap).filter((c) => c.opstart || c.omzet || c.pk || c.bijverkoop || c.wow).length;

  return (
    <div data-testid="locatie-call-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3 md:mb-4">
        <div>
          <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-900">Locatie-call</h1>
          <p className="text-sm text-zinc-500">Per locatie, in de volgorde van het gesprek.
            {uitlegGezien !== null && !uitleg && <> · <button type="button" data-testid="locatiecall-uitleg-link" onClick={() => setUitleg(true)} className="font-semibold text-blue-700 hover:text-blue-900">Uitleg</button></>}</p>
          {callWeek && <p data-testid="locatiecall-callweek" className="text-sm font-semibold text-zinc-800 mt-0.5">Calls van {callWeek.callLabelLang} · over week {callWeek.overWeekNr} <span className="font-normal text-zinc-500">— de cijfers van week {callWeek.overWeekNr}, de afspraken voor deze week</span></p>}
          {kaartGeldig && kaartData.isCallDag && kaartData.voortgang?.totaal > 0 && (
            <div data-testid="belvoortgang" className="mt-2 max-w-xs">
              <p className="text-xs font-semibold text-zinc-500"><span data-testid="belvoortgang-tekst">{kaartData.voortgang.compleet} van {kaartData.voortgang.totaal} klaar</span></p>
              <div className="h-1 rounded-full bg-zinc-200 mt-1 overflow-hidden"><div className="h-full bg-blue-600 transition-[width] duration-300" style={{ width: `${kaartData.voortgang.totaal ? (100 * kaartData.voortgang.compleet) / kaartData.voortgang.totaal : 0}%` }} /></div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isOm && <MijnLocatiesSelect username={user.username} locations={meta.locations} error={!!metaError} />}
          {(user.role === "oversight" || user.role === "om") && <OmSelector om={om} setOm={setOm} oms={meta.oms} inactiveOms={meta.inactiveOms} />}
          {week && <WeekPicker week={week} setWeek={setWeek} sub={callWeek ? callWeekTekst(callWeek) : undefined} />}
        </div>
      </div>

      <VorigeCallBalk vorigeWeek={meta.standaardCallWeek && meta.standaardCallWeek !== meta.huidigeCallWeek && week === meta.standaardCallWeek ? meta.huidigeCallWeek : null} week={week} om={om} locaties={visibleLocations} onOpen={setWeek} />

      {uitleg && <LocatieCallUitleg callWeek={callWeek} vanafLabel={meta?.callGrens?.klaarRegelsVanafLabel} oversight={user.role === "oversight"} onBegrepen={begrepen} />}

      {(metaError || callsError || blScoresError) && (
        <div className="mb-4 space-y-2">
          <LoadError error={metaError} onRetry={loadMeta} testid="locatiecall-meta-error" />
          <LoadError error={callsError} onRetry={loadCalls} testid="locatiecall-load-error" />
          <LoadError error={blScoresError} onRetry={loadBlScores} testid="locatiecall-blscores-error" />
        </div>
      )}

      {om && om !== user.username && !(meta.inactiveOms || []).includes(om) && (
        <div data-testid="alleen-lezen-banner" className="mb-4 flex items-center gap-2 rounded-md bg-blue-50 border border-blue-200 px-4 py-2.5">
          <User className="h-4 w-4 text-blue-600 shrink-0" />
          <span className="text-sm text-blue-900">Je bekijkt de calls van <span className="font-semibold capitalize">{om}</span> — alleen lezen.</span>
        </div>
      )}

      {om && (meta.inactiveOms || []).includes(om) && (
        <div data-testid="inactieve-om-banner" className="mb-4 flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-4 py-2.5">
          <User className="h-4 w-4 text-amber-600 shrink-0" />
          <span className="text-sm text-amber-800 font-semibold capitalize">{om}</span>
          <span className="text-sm text-amber-800">is uit dienst — je bekijkt de historie (alleen-lezen).</span>
        </div>
      )}

      {geenToewijzing ? (
        <div data-testid="geen-locaties-toegewezen" className="mb-3 md:mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3.5 flex items-start gap-2.5">
          <AlertTriangle className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900">
              {om === user.username ? "Er zijn nog geen locaties aan je toegewezen." : `Er zijn nog geen locaties aan ${om} toegewezen.`}
            </p>
            <p className="text-sm text-amber-900 mt-0.5">
              {om === user.username ? "Vraag Johannes of Milos om je locaties toe te wijzen in Instellingen → Locaties & OM-toewijzing." : `Toewijzen gebeurt in Instellingen → Locaties & OM-toewijzing.`}
            </p>
            {user.role === "oversight" && (
              <a data-testid="link-locatietoewijzing" href="/instellingen"
                className="inline-flex items-center gap-1 mt-2 text-sm font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-700">
                Naar OM-locatietoewijzing
              </a>
            )}
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:gap-3 mb-3 md:mb-4">
        <div className={`${TEGEL} ${TEGEL_WIT}`}>
          <div className={TEGEL_LABEL}>Aangeraakt</div>
          <div className={`${TEGEL_GETAL} text-zinc-900`}>{doneCount}<span className="text-zinc-400">/{visibleLocations.length}</span></div>
        </div>
        <div className={`${TEGEL} ${roodCount > 0 ? TEGEL_ROOD : TEGEL_WIT}`}>
          <div className={TEGEL_LABEL}>Rood</div>
          <div data-testid="locatiecall-rood-count" className={`${TEGEL_GETAL} ${getalKleur(roodCount, true)}`}>{roodCount}</div>
        </div>
        <div className={`${TEGEL} ${rzaCount > 0 ? TEGEL_ROOD : TEGEL_WIT}`}>
          <div className={TEGEL_LABEL}>Rood zonder afspraak</div>
          <div data-testid="locatiecall-rza-count" className={`${TEGEL_GETAL} ${getalKleur(rzaCount, true)}`}>{rzaCount}</div>
        </div>
        <div className={`${TEGEL} ${TEGEL_WIT}`}>
          <div className={TEGEL_LABEL}>Status</div>
          <div className={`flex items-center gap-1.5 text-sm font-semibold mt-1.5 ${rzaCount > 0 ? "text-rose-600" : "text-emerald-600"}`}>
            {rzaCount > 0 ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
            {rzaCount > 0 ? "Afspraak nodig" : "Geen rood zonder afspraak"}
          </div>
        </div>
      </div>
      )}

      {allesWeggefilterd && (
        <div data-testid="alles-weggefilterd" className="mb-4 rounded-[10px] border border-zinc-200 bg-white px-4 py-3">
          <p className="text-sm text-zinc-500">
            Je hebt wel locaties toegewezen, maar je huidige selectie bij "Mijn locaties" laat er geen zien.
            Pas de selectie aan om ze te bekijken.
          </p>
        </div>
      )}

      {!geenToewijzing && <NietAangeleverd />}

      {rzaCount > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-md bg-rose-50 border border-rose-200 px-4 py-2.5">
          <AlertTriangle className="h-4 w-4 text-rose-600" />
          <span className="text-sm text-rose-800 font-semibold" data-testid="rood-summary">{rzaCount === 1 ? "1 locatie" : `${rzaCount} locaties`} rood zonder afspraak.</span>
        </div>
      )}

      {kaartErr && <div className="mb-3"><FoutRegel testid="kaartinfo-error" tekst="Kaartinformatie (aanlevering, vorige call, 13 weken) kon niet geladen worden" onRetry={() => setKaartVersie((v) => v + 1)} /></div>}
      <div className="space-y-3">
        {week && visibleLocations.map((loc) => (
          <LocationCard key={`${week}|${om}|${loc}`} lades={lades} call={getCall(loc)} meta={meta} checks={kaartGeldig ? kaartData.checks : null} nietDeze={kaartGeldig ? kaartData.nietDezeWeek : null} kaartErr={kaartErr} editable={editable && loaded} loading={editable && !loaded} geladen={loaded} onSave={onSave} onMerge={mergeCall} onAddAfspraak={onAddAfspraak} open={!isCollapsed(loc)} onToggle={() => toggle(loc)} blScore={blScores[loc]} kaartInfo={kaartInfo[loc]} overWeekNr={callWeek?.overWeekNr} onNext={onNext} lopendVersie={lopendVersie} />
        ))}
      </div>

      <AfspraakDialog open={dialogOpen} onOpenChange={setDialogOpen} week={week || currentWeek()} locaties={meta.locations} generalBuckets={meta.generalBuckets} oms={meta.oms || []} internKandidaten={meta.internKandidaten || []} blContact={meta.blContact} afdelingen={meta.afdelingen} defaultLocatie={dialogLocatie} defaultWat={dialogWat} onCreated={() => { loadCalls(); setLopendVersie((v) => v + 1); }} />
    </div>
  );
}
